import { notifyLumenToast } from '@components'

import { trackError } from '@billy/track-error'
import download from 'downloadjs'
import { useCallback, useRef, useState } from 'react'

import type { BalanceExportFormat, ExportFileResult } from '@features/balanceLedger/types/balanceExport'

import { NotificationKeys } from '../../../enums/notificationKeys'

interface UseExportFileDownloadProps {
  isReady: boolean
  notificationKey: NotificationKeys
  errorMessage: string
  fetchExport: (format: BalanceExportFormat) => Promise<ExportFileResult>
  // Called only once the file has actually been downloaded, so callers can track success without
  // reporting a false positive when the export fails.
  onSuccess?: (format: BalanceExportFormat) => void
}

interface UseExportFileDownloadResult {
  downloadExport: (format: BalanceExportFormat) => Promise<void>
  isExporting: boolean
}

// Shared download/loading/error-handling scaffolding for the suppliers balance overview and statement exports;
// callers only need to supply how to fetch the file for a given format.
export const useExportFileDownload = ({
  isReady,
  notificationKey,
  errorMessage,
  fetchExport,
  onSuccess,
}: UseExportFileDownloadProps): UseExportFileDownloadResult => {
  const [exportingFormat, setExportingFormat] = useState<BalanceExportFormat | null>(null)
  const isExportingRef = useRef(false)

  const downloadExport = useCallback(
    async (format: BalanceExportFormat) => {
      if (!isReady || isExportingRef.current) {
        return
      }

      isExportingRef.current = true
      setExportingFormat(format)

      try {
        const { blob, fileName } = await fetchExport(format)

        download(blob, fileName)
        onSuccess?.(format)
      } catch (error: unknown) {
        trackError(error)
        notifyLumenToast({
          id: notificationKey,
          message: errorMessage,
          variant: 'danger',
        })
      } finally {
        isExportingRef.current = false
        setExportingFormat(null)
      }
    },
    [errorMessage, fetchExport, isReady, notificationKey, onSuccess],
  )

  return {
    downloadExport,
    isExporting: exportingFormat !== null,
  }
}
